import { useEffect } from "react";
import { subscribeEvents, useGameStore } from "../app/gameStore";
import { useSettingsStore } from "../app/settingsStore";
import { applyVolume, playCue, unlockAudio } from "./AudioEngine";
import { cueForEvent } from "./registry";

/**
 * Wires the audio engine to the game, once, from the composition root.
 *
 * It listens to the same event feed the FX layer does — so a hand-fired effect
 * from the tuning console makes its real noise, and there is exactly one place
 * where "what just happened" is turned into presentation.
 */
export function useAudio(): void {
  const muted = useSettingsStore((s) => s.muted);
  const volume = useSettingsStore((s) => s.volume);

  // Autoplay policy: no context may exist until the player has touched the
  // page. Any gesture will do, and the listeners retire after the first one.
  useEffect(() => {
    const start = () => unlockAudio();
    const opts = { passive: true } as const;
    window.addEventListener("pointerdown", start, opts);
    window.addEventListener("keydown", start, opts);
    return () => {
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
    };
  }, []);

  useEffect(() => {
    applyVolume();
  }, [muted, volume]);

  useEffect(
    () =>
      subscribeEvents((event) => {
        const cue = cueForEvent(event);
        if (cue) playCue(cue);
      }),
    [],
  );

  // A refusal is a UI outcome rather than a simulation event — the engine
  // declined to do anything, so there is nothing for it to emit — but it is
  // exactly the moment a player needs telling without reading the toast.
  useEffect(
    () =>
      useGameStore.subscribe((state, previous) => {
        if (state.rejection && state.rejection !== previous.rejection) playCue("refused");
      }),
    [],
  );
}
